import { toast as sonnerToast } from "sonner";

import { cn } from "../../../utils";
import { Button } from "../../Button";
import { Icon, type KyanIcon } from "../../Icon";
import { Typography } from "../../Typography";
import { type ToastProps, type ToastVariant } from "../Toaster.types";

export const ToasterContent = ({
  id,
  title,
  description,
  variant = "info",
  action,
}: ToastProps) => {
  const isInfo = variant === "info";
  const isSuccess = variant === "success";
  const isWarning = variant === "warning";
  const isError = variant === "error";
  const isPending = variant === "pending";
  const isLiquidationNotice = variant === "liquidationNotice";
  const isLiquidationWarning = variant === "liquidationWarning";
  const isLiquidationError = variant === "liquidationError";

  const iconName = (
    {
      info: "bell",
      liquidationNotice: "notice",
      success: "checkmark-circle",
      warning: "attention",
      pending: "clock",
      liquidationWarning: "attention",
      liquidationError: "attention",
      error: "error-circle",
    } as Record<ToastVariant, KyanIcon>
  )[variant];

  return (
    <div
      className={cn(
        "ui:min-h-16 ui:w-96 ui:rounded-xl ui:border-l-2 ui:bg-white ui:p-4 ui:shadow-[0_4px_12px_rgba(0,0,0,0.1)] ui:dark:bg-buttonFill",
        {
          "ui:border-brandBlue": isInfo || isLiquidationNotice || isPending,
          "ui:border-success": isSuccess,
          "ui:border-warning": isWarning || isLiquidationWarning,
          "ui:border-error": isError || isLiquidationError,
        },
      )}
    >
      <div className="ui:flex ui:items-center ui:gap-4">
        <Icon
          name={iconName}
          className={cn(
            "ui:grid ui:size-8 ui:min-w-8 ui:place-items-center ui:rounded-sm ui:text-sm",
            {
              "ui:text-brandBlue": isPending,
              "ui:bg-brandBlue/10 ui:text-brandBlue":
                isInfo || isLiquidationNotice,
              "ui:text-success": isSuccess,
              "ui:bg-warning/10 ui:text-warning":
                isWarning || isLiquidationWarning,
              "ui:text-error": isError || isLiquidationError,
              "ui:bg-error/10": isLiquidationError,
            },
            {
              "ui:text-3xl": ["success", "error"].includes(variant),
            },
          )}
        />

        <div className="ui:grid ui:gap-2">
          <div className="ui:grid ui:gap-1">
            <Typography medium primary>
              {title}
            </Typography>
            {description && <Typography size={12}>{description}</Typography>}
          </div>
          {action && (
            <Button
              variant="link"
              className="ui:w-fit"
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          )}
        </div>

        <Icon
          size="sm"
          name="cross"
          hoverEffect="circle"
          className="ui:ml-auto ui:p-2 ui:text-textSecondary"
          onClick={() => {
            sonnerToast.dismiss(id);
          }}
        />
      </div>
    </div>
  );
};
